import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { AccountabilityFooter } from '../components/AccountabilityFooter';
import { FixedFacts } from '../components/FixedFacts';
import { GoalForm } from '../components/GoalForm';
import { GeenRapport, Storing } from '../components/MeasurementState';
import { ThresholdForm } from '../components/ThresholdForm';
import { FOCUSRING } from '../focus';
import { isObject, isTelling } from '../format';
import { useEenMeting } from '../hooks/useEenMeting';
import { useInstellingen } from '../hooks/useInstellingen';
import { useSignaleringen } from '../hooks/useOverzicht';
import { useOwnWeek } from '../hooks/useOwnWeek';
import { STORING_MISLUKT, weektoestand } from '../toestand';
import { afgeslotenWeken } from '../week';
import type { EigenAfbakening, Meetversies, SignaleringenAfbakening } from '../work-quality.api';

/**
 * Instellingen — drempel en doel verzetten met het effect in beeld (story 4.5, story 12, S7).
 *
 * Drie blokken onder elkaar: het Drempelformulier, het Doelformulier en wat vastligt. Elk formulier
 * toont wat zijn waarde in de laatst afgesloten week zou doen, met namen per werksoort.
 *
 * **De week is de laatst afgesloten week, en er staat geen periodekiezer.** De week staat wél in de
 * zinnen, zodat te lezen is waarover het effect gaat.
 *
 * **De pagina werpt nooit** (story 12, Robuustheid). Een week waarvan de `afbakening` geen object is,
 * toont in het vastligtblok de storing en laat de Voettekst weg; de formulieren blijven bruikbaar.
 *
 * **Het scherm kent de vier uitgesloten velden en de signaleringsgrens niet uit zichzelf** (AD-5):
 * het leest ze uit de antwoorden waarin de dienst ze meestuurt — de week van de aanvrager voor de
 * velden, het signaleringsantwoord voor de factor en zijn ondergrens. Die twee zijn allebei
 * metingen, dus AD-24 geldt: lopen hun versienummers uiteen, dan wordt dat blok opnieuw opgehaald
 * in plaats van twee lezingen naast elkaar te zetten.
 *
 * **Ook dit scherm draagt de verantwoordingsvoet** (story 4.6). `EXPERIENCE.md` schrijft hem "onder
 * elke weergave" voor; de dekkingstabel wees UX-DR15 niet aan story 4.5 toe, waardoor hij hier als
 * enige ontbrak. De afbakening komt uit de week die dit scherm toont, dus de voet spreekt over
 * dezelfde meting als de zinnen erboven — en staat er niet zolang die week niet gelezen is.
 */
export function WorkQualitySettingsPage() {
  const { t } = useTranslation();
  // De laatst afgesloten week. Niet "deze week": een halve week meten levert een halve uitkomst die
  // als een hele leest (UX-DR16).
  const weken = afgeslotenWeken(new Date());
  const week = weken[0] ?? '';

  const instellingen = useInstellingen();
  const eigen = useOwnWeek(week);
  const signalen = useSignaleringen(week);

  const eigenWeek = eigen.data?.data ?? null;
  const signaleringen = signalen.data?.data ?? null;
  // Alleen een afbakening die een object is, is te lezen; al het andere is een onleesbare week.
  const eigenAfbakening: EigenAfbakening | undefined = isObject(eigenWeek?.afbakening) ? eigenWeek.afbakening : undefined;
  const signaalAfbakening: SignaleringenAfbakening | undefined = isObject(signaleringen?.afbakening)
    ? signaleringen.afbakening
    : undefined;
  const onleesbaar =
    (eigenWeek !== null && eigenAfbakening === undefined) || (signaleringen !== null && signaalAfbakening === undefined);

  const onderdelen: Meetversies[] = [eigenAfbakening, signaalAfbakening].filter(
    (afbakening): afbakening is NonNullable<typeof afbakening> => afbakening !== undefined,
  );

  const { refetch: herlaadEigen } = eigen;
  const { refetch: herlaadSignalen } = signalen;
  const opnieuw = useCallback(() => {
    herlaadEigen();
    herlaadSignalen();
  }, [herlaadEigen, herlaadSignalen]);

  const lopenUiteen = useEenMeting(onderdelen, opnieuw);
  // De voorwaarde waaronder dit scherm één meting toont, en dus ook waaronder de verantwoording
  // over die ene meting mag spreken.
  const feitenZijnEenMeting =
    week !== '' &&
    !lopenUiteen &&
    !eigen.isError &&
    !signalen.isError &&
    !eigen.isLoading &&
    !signalen.isLoading &&
    eigenAfbakening !== undefined &&
    signaalAfbakening !== undefined;

  const geladen = instellingen.data?.data;
  // Bruikbaar = beide waarden zijn tellingen. Een herlezing die faalt terwijl er al bruikbare data is,
  // laat de formulieren staan: anders verdwijnt wat iemand aan het typen was.
  const bruikbaar =
    isTelling(geladen?.drempelProductenPerPersoon) && isTelling(geladen?.doelInEenKeerGoed) ? geladen : null;
  // Het bereik van de dienst, begrensd op 20-100 zoals de proxy het hard aanhoudt (M3).
  const drempelMin = Math.max(20, isTelling(bruikbaar?.drempelMinimum) ? bruikbaar.drempelMinimum : 20);
  const drempelMax = Math.min(100, isTelling(bruikbaar?.drempelMaximum) ? bruikbaar.drempelMaximum : 100);

  function formulieren() {
    if (bruikbaar !== null) {
      return (
        <>
          <ThresholdForm huidigeDrempel={bruikbaar.drempelProductenPerPersoon} max={drempelMax} min={drempelMin} week={week} />
          <GoalForm huidigDoel={bruikbaar.doelInEenKeerGoed} week={week} />
        </>
      );
    }
    if (instellingen.isLoading) {
      return (
        <div aria-busy="true" className="flex flex-col gap-5" data-testid="werkkwaliteit-instellingen-laden">
          <span className="sr-only">{t('qa.eigen.laden')}</span>
          <div aria-hidden="true" className="h-40 rounded-xl bg-[var(--background-muted)]" />
          <div aria-hidden="true" className="h-40 rounded-xl bg-[var(--background-muted)]" />
        </div>
      );
    }
    // Ook een antwoord dat wél binnenkwam maar geen bruikbare waarden draagt is hier een storing: een
    // invoerveld met een lege of onzinnige beginwaarde zou iemand laten opslaan zonder te zien wat er stond.
    return (
      <section
        className="rounded-[10px] bg-[var(--destructive-light)] px-4 py-3 text-[13px] text-[var(--foreground)]"
        data-werkkwaliteit-sectie="drempel"
        data-testid="werkkwaliteit-drempel-storing"
        role="alert"
      >
        <span aria-hidden="true" className="material-symbols-outlined mr-1.5 align-[-3px] text-[16px]">
          error
        </span>
        {t('qa.instellingen.drempelStoring')}
      </section>
    );
  }

  function vastligtblok() {
    if (week === '') {
      return (
        <p className="text-[13px] text-[var(--foreground-muted)]" data-testid="werkkwaliteit-vastligt-geen-week">
          {t('qa.instellingen.vastligtGeenWeek')}
        </p>
      );
    }
    // De storing gaat vóór het laden. Andersom verbergt één nog lopende lezing de storing van de
    // andere achter een skeleton, en dan leest een storing als "even geduld" — precies de verwarring
    // die AD-25 en AD-27 uit deze module houden.
    if (eigen.isError || signalen.isError) {
      return (
        <p
          className="rounded-[10px] bg-[var(--destructive-light)] px-4 py-3 text-[13px] text-[var(--foreground)]"
          data-testid="werkkwaliteit-vastligt-storing"
          role="alert"
        >
          <span aria-hidden="true" className="material-symbols-outlined mr-1.5 align-[-3px] text-[16px]">
            error
          </span>
          {t('qa.instellingen.vastligtStoring')}
        </p>
      );
    }
    if (eigen.isLoading || signalen.isLoading) {
      return (
        <div
          className="h-32 rounded-xl bg-[var(--background-muted)]"
          data-testid="werkkwaliteit-vastligt-laden"
          role="status"
        >
          <span className="sr-only">{t('qa.eigen.laden')}</span>
        </div>
      );
    }
    if (eigen.data === undefined || signalen.data === undefined || onleesbaar) {
      return (
        <p
          className="rounded-[10px] bg-[var(--destructive-light)] px-4 py-3 text-[13px] text-[var(--foreground)]"
          data-testid="werkkwaliteit-vastligt-storing"
          role="alert"
        >
          <span aria-hidden="true" className="material-symbols-outlined mr-1.5 align-[-3px] text-[16px]">
            error
          </span>
          {t('qa.instellingen.vastligtStoring')}
        </p>
      );
    }
    // Een week zonder meting is geen storing en ook geen antwoord dat de feiten niet noemt: het is
    // de toestand die de rest van deze module met `GeenRapport` toont, mét de datum van de laatste
    // geslaagde run (AD-25). Zonder dit zou dit blok zeggen dat het antwoord de velden niet noemt,
    // terwijl het blok erboven voor dezelfde week zegt dat er nog niets is gemeten.
    // Ook hier geldt AD-25: `meting_mislukt` is een storing en geen week waarin niets gemeten is.
    // Dit scherm was tot en met story 4.5 het enige dat die twee op één hoop gooide.
    if (eigen.data.data === null || signalen.data.data === null) {
      const ongemeten = eigen.data.data === null ? eigen.data : signalen.data;
      return weektoestand(ongemeten.reden) === 'nog_niet_gemeten' ? (
        <GeenRapport
          laatsteGeslaagdeRun={ongemeten.laatsteGeslaagdeRun ?? null}
          metingIngeschakeld={ongemeten.metingIngeschakeld ?? null}
          volgendeRun={ongemeten.volgendeRun ?? null}
        />
      ) : (
        // Dit scherm kent geen weekkiezer en draagt geen week in het adres: het toont
        // altijd de nieuwste afgesloten week. Een link naar een andere week zou hier
        // niets doen — de lezer klikt en blijft staan — dus die staat er niet.
        <Storing
          fout={ongemeten.fout ?? null}
          laatsteGeslaagdeRun={ongemeten.laatsteGeslaagdeRun ?? null}
          oorzaak={STORING_MISLUKT}
          terugweg={false}
        />
      );
    }
    if (lopenUiteen) {
      // `useEenMeting` haalt één keer per versiecombinatie opnieuw op — meer zou een lus worden.
      // Komt dezelfde combinatie terug, dan hoort de lezer zelf een tweede poging te kunnen doen.
      return (
        <div className="flex flex-col items-start gap-2" data-testid="werkkwaliteit-vastligt-herladen" role="status">
          <p className="text-[13px] text-[var(--foreground)]">{t('qa.eigen.metingHerladen')}</p>
          <button
            className={`rounded-lg border border-[var(--border)] bg-[var(--background)] px-3 py-1.5 text-[13px] font-medium text-[var(--foreground-heading)] hover:bg-[var(--background-muted)] max-sm:min-h-[44px] ${FOCUSRING}`}
            data-testid="werkkwaliteit-vastligt-opnieuw"
            onClick={opnieuw}
            type="button"
          >
            {t('qa.eigen.metingHerladenOpnieuw')}
          </button>
        </div>
      );
    }

    const velden = eigenAfbakening?.uitgeslotenVelden;

    return (
      <FixedFacts
        minimumBeoordeeldeProducten={signaalAfbakening?.minimumBeoordeeldeProducten}
        signaleringsfactor={signaalAfbakening?.signaleringsfactor}
        // Alleen tekst: een object of `null` in de lijst zou React laten werpen.
        uitgeslotenVelden={Array.isArray(velden) ? velden.filter((veld): veld is string => typeof veld === 'string') : null}
      />
    );
  }

  return (
    <div className="flex min-w-0 max-w-[720px] flex-col gap-5" data-testid="werkkwaliteit-instellingen">
      {formulieren()}
      {/* De naam van de sectie hangt niet aan de laadtoestand: ook een skeleton of een storing hoort
          bij een blok dat een naam heeft. */}
      <section aria-label={t('qa.instellingen.vastligtKop')} data-werkkwaliteit-sectie="vastligt">
        {vastligtblok()}
      </section>
      {/* De voet hangt aan exact dezelfde voorwaarde als de vastliggende feiten erboven. Alleen
          "de week is gelezen" is niet genoeg: lopen de twee metingen uiteen (AD-24), dan zou de
          voet de afbakening van één van beide afdrukken terwijl het blok erboven juist zegt dat ze
          niet naast elkaar gelegd mogen worden. */}
      {feitenZijnEenMeting && eigenAfbakening !== undefined && <AccountabilityFooter afbakening={eigenAfbakening} />}
    </div>
  );
}
